import {
  useCreatePresetMutationApi,
  useReadPresetApi,
  useUpdatePresetMutationApi,
} from '@aibees/shared/api';
import { Icon } from '@aibees/shared/components';
import { notificationIconMap } from '@aibees/shared/features/notifications/config';
import { useNotify } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { LoadingButton } from '@mui/lab';
import { Button, Stack } from '@mui/material';
import { random } from 'lodash-es';
import { memo, useEffect, useMemo } from 'react';
import { useTemplateContext } from '../../context';
import { EditData, OperationMode } from '../../types';
import * as Styled from './styles';
import * as SX from './sx';
import type { DialogFooterProps as Props } from './types';

export const DialogFooter = memo(
  ({
    className,
    dataType,
    mode,
    templateName,
    selectedTemplate,
    columns,
    visibility,
    onClose,
    setSelectedTemplate,
  }: Props) => {
    const {
      transferredData,
      handleInitEditData,
      editValue,
      totalSelectedOptions,
      isResetDisabled,
      hasChanges,
      updateDraftWithEditValue,
      handleEnterName,
      handleInitItems,
      handleReset,
      handleClearAllCategories,
    } = useTemplateContext();
    const isEditMode = mode === OperationMode.EDIT;
    const notify = useNotify();
    // Read the exact template being edited by id. Browsing by name returned
    // every preset whose name matched (duplicates share names) and picking
    // content[0] often loaded the wrong one.
    const { data: editPreset } = useReadPresetApi<any>(selectedTemplate?.id, {
      enabled: !!selectedTemplate?.id,
    });

    const disabled = useMemo(
      () =>
        isEditMode
          ? templateName === selectedTemplate?.name &&
            visibility === selectedTemplate?.visibility &&
            !hasChanges
          : !templateName || !totalSelectedOptions,
      [
        isEditMode,
        templateName,
        selectedTemplate?.name,
        selectedTemplate?.visibility,
        visibility,
        hasChanges,
        totalSelectedOptions,
      ]
    );

    useEffect(() => {
      if (isEditMode) {
        updateDraftWithEditValue();
      }
    }, [isEditMode, editValue]);

    useEffect(() => {
      if (isEditMode && !editValue && editPreset) {
        handleInitEditData(editPreset as EditData);
      }
    }, [editPreset, isEditMode, editValue]);

    useEffect(() => {
      handleInitItems(columns.items, columns.information);
    }, []);

    const apiCreate = useCreatePresetMutationApi(false);
    const apiUpdate = useUpdatePresetMutationApi<any>(
      editValue?.id || '',
      1000
    );

    const { isLoading, mutateAsync } = isEditMode ? apiUpdate : apiCreate;

    const handleSave = () => {
      const data = {
        name: templateName,
        type: dataType,
        visibility,
        value: transferredData,
      } as any;

      mutateAsync(data)
        .then((res) => {
          handleInitEditData(res as EditData);
          onClose();
          handleEnterName(templateName || '');
          setSelectedTemplate?.(res);

          notify.complete(
            `${
              isEditMode ? 'Export Template Updated' : 'Export Template Saved'
            }`,
            `${
              isEditMode
                ? 'Your changes have been saved and applied to this template.'
                : 'The export template has been added successfully.'
            }`,
            random(1, 101),
            {
              icon: notificationIconMap.CSVTemplate!,
            }
          );
        })
        .catch((err) => notify.error(err));
    };

    return (
      <Styled.Wrapper
        className={classnames('DialogFooter', className)}
        sx={SX.root}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Icon name="history" />}
            disabled={isResetDisabled}
            onClick={handleReset}
          >
            Reset to Default
          </Button>

          <LoadingButton
            variant="outlined"
            size="small"
            startIcon={<Icon name="eraser" />}
            onClick={handleClearAllCategories}
            disabled={!totalSelectedOptions}
          >
            Clear All
          </LoadingButton>
        </Stack>

        <Stack direction="row" alignItems="center" gap={1}>
          <Button variant="text" color="default" size="small" onClick={onClose}>
            Cancel
          </Button>

          <LoadingButton
            variant="contained"
            color="primary"
            size="small"
            onClick={handleSave}
            loading={isLoading}
            disabled={disabled}
          >
            Save
          </LoadingButton>
        </Stack>
      </Styled.Wrapper>
    );
  }
);

DialogFooter.displayName = 'DialogFooter';
